import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import LumivetaLogo from '../../components/LumivetaLogo';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';

const Register = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const { language, setLanguage, t, isSpanish } = useLanguage();
  const formRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Password visibility toggles
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // Correspondence language preference (separate from app UI language)
  const [emailLanguage, setEmailLanguage] = useState(language);
  
  // Welcome toast state
  const [showWelcomeToast, setShowWelcomeToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError(isSpanish ? 'Las contraseñas no coinciden' : 'Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      setError(isSpanish ? 'La contraseña debe tener al menos 8 caracteres con un número' : 'Password must be at least 8 characters with a number');
      return;
    }

    setLoading(true);

    try {
      await register(formData.email, formData.password, formData.name, emailLanguage);
      
      // Redirect with welcome params - toast shows on subscription page above modal
      navigate(`/subscription-plans?welcome=true&lang=${emailLanguage}`);
    } catch (err) {
      setError(err.message || (isSpanish ? 'Error en el registro' : 'Registration failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>{isSpanish ? 'Comienza Tu Prueba Gratuita' : 'Start Your Free Trial'} - Lumiveta</title>
        <meta name="description" content={isSpanish ? 'Únete a Lumiveta para orientación bíblica personalizada' : 'Join Lumiveta for personalized biblical guidance and spiritual support'} />
      </Helmet>

      <div className="min-h-dvh bg-background">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-2xl mx-auto">
            {/* Language Toggle - Top Right */}
            <div className="flex justify-end mb-4">
              <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
                <button
                  onClick={() => setLanguage('en')}
                  className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                    language === 'en' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
                  }`}
                  data-testid="lang-en"
                >
                  EN
                </button>
                <button
                  onClick={() => setLanguage('es')}
                  className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                    language === 'es' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
                  }`}
                  data-testid="lang-es"
                >
                  ES
                </button>
              </div>
            </div>
            
            {/* Logo */}
            <div className="text-center mb-8">
              <Link to="/" className="inline-block">
                <LumivetaLogo size="large" />
              </Link>
            </div>

            {/* ============== STEP 1: TRIAL VALUE CARD (TOP) ============== */}
            <div className="glass rounded-2xl border-2 border-primary/50 overflow-hidden mb-8 ring-2 ring-primary/30">
              {/* Card Header - Trial Offer */}
              <div className="p-6 bg-primary/10 text-center">
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
                    <Icon name="Gift" size={32} color="#FFD700" />
                  </div>
                </div>
                <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground mb-2">
                  {isSpanish ? 'Prueba Gratuita de 7 Días' : '7-Day Free Trial'}
                </h1>
                <p className="text-primary font-medium text-lg mb-4">
                  {isSpanish ? 'Incluye Prueba Gratuita de 7 Días - Sin Riesgo' : 'Includes 7-Day Free Trial - Risk Free'}
                </p>
                
                {/* Price Display */}
                <div className="mb-4">
                  <span className="text-5xl font-heading font-bold text-primary">$0.00</span>
                  <span className="text-xl text-muted-foreground ml-2">{isSpanish ? 'Hoy' : 'Today'}</span>
                </div>
                <p className="text-muted-foreground">
                  {isSpanish 
                    ? 'Después $6.99/mes después de 7 días' 
                    : 'Then $6.99/mo after 7 days'}
                </p>
              </div>

              {/* Card Body - Features */}
              <div className="p-6">
                <ul className="space-y-3 mb-6">
                  <li className="flex items-center gap-3">
                    <Icon name="Check" size={18} color="#10B981" className="flex-shrink-0" />
                    <span className="text-foreground">
                      {isSpanish ? '7 reflexiones diarias con guía espiritual' : '7 daily reflections with spiritual guidance'}
                    </span>
                  </li>
                  <li className="flex items-center gap-3">
                    <Icon name="Check" size={18} color="#10B981" className="flex-shrink-0" />
                    <span className="text-foreground">
                      {isSpanish ? 'Pipeline completo de 7 partes' : 'Full 7-part response pipeline'}
                    </span>
                  </li>
                  <li className="flex items-center gap-3">
                    <Icon name="Check" size={18} color="#10B981" className="flex-shrink-0" />
                    <span className="text-foreground">
                      {isSpanish ? 'Historial "Mi Mina de Oro"' : '"My Gold Mine" reflection history'}
                    </span>
                  </li>
                  <li className="flex items-center gap-3">
                    <Icon name="Check" size={18} color="#10B981" className="flex-shrink-0" />
                    <span className="text-foreground">
                      {isSpanish ? 'Cancela en cualquier momento' : 'Cancel anytime'}
                    </span>
                  </li>
                </ul>

                {/* Primary CTA Button */}
                <Button
                  variant="default"
                  fullWidth
                  onClick={scrollToForm}
                  className="gold-glow text-lg py-6"
                  data-testid="claim-trial-btn"
                >
                  <Icon name="Gift" size={20} className="mr-2" />
                  {isSpanish ? 'Reclamar Mi Prueba Gratuita de 7 Días' : 'Claim My 7-Day Free Trial'}
                </Button>
              </div>
            </div>

            {/* ============== STEP 2: REGISTRATION FORM ============== */}
            <div ref={formRef} className="glass rounded-2xl border border-primary/20 p-6 lg:p-8">
              <div className="mb-6">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                    <span className="text-primary font-bold text-sm">2</span>
                  </div>
                  <h2 className="font-heading font-semibold text-2xl text-foreground">
                    {isSpanish ? 'Crea Tu Cuenta' : 'Create Your Account'}
                  </h2>
                </div>
                <p className="text-muted-foreground ml-11">
                  {isSpanish ? 'Únete a nuestra comunidad en menos de 2 minutos' : 'Join our community in less than 2 minutes'}
                </p>
              </div>

              {error && (
                <div className="mb-6 p-4 bg-error/10 border border-error/20 rounded-lg">
                  <p className="text-sm text-error">{error}</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    {isSpanish ? 'Nombre Completo' : 'Full Name'}
                  </label>
                  <Input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder={isSpanish ? 'Tu nombre completo' : 'Your full name'}
                    required
                    minLength={2}
                    className="bg-input border-border"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    {isSpanish ? 'Correo Electrónico' : 'Email Address'}
                  </label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder={isSpanish ? 'tu@correo.com' : 'your@email.com'}
                    required
                    className="bg-input border-border"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    {isSpanish ? 'Contraseña' : 'Password'}
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="••••••••"
                      required
                      minLength={6}
                      className="flex h-10 w-full rounded-md border border-input bg-input px-3 py-2 pr-10 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      data-testid="register-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors z-10"
                      tabIndex={-1}
                      data-testid="toggle-password-visibility"
                    >
                      <Icon name={showPassword ? "EyeOff" : "Eye"} size={18} />
                    </button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {isSpanish ? 'Al menos 8 caracteres con un número' : 'At least 8 characters with a number'}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    {isSpanish ? 'Confirmar Contraseña' : 'Confirm Password'}
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      placeholder="••••••••"
                      required
                      className="flex h-10 w-full rounded-md border border-input bg-input px-3 py-2 pr-10 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      data-testid="register-confirm-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors z-10"
                      tabIndex={-1}
                      data-testid="toggle-confirm-password-visibility"
                    >
                      <Icon name={showConfirmPassword ? "EyeOff" : "Eye"} size={18} />
                    </button>
                  </div>
                </div>

                {/* Correspondence Language */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    {isSpanish ? 'Idioma de Correspondencia' : 'Correspondence Language'}
                  </label>
                  <p className="text-xs text-muted-foreground mb-2">
                    {isSpanish 
                      ? 'Elige como recibiras correos de LUMIVETA'
                      : 'Choose how you receive emails from LUMIVETA'}
                  </p>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setEmailLanguage('en')}
                      data-testid="register-email-lang-en"
                      className={`flex-1 py-2 px-4 rounded-lg border transition-colors font-medium ${
                        emailLanguage === 'en' 
                          ? 'bg-primary text-primary-foreground border-primary' 
                          : 'border-border text-muted-foreground hover:border-primary/50'
                      }`}
                    >
                      English
                    </button>
                    <button
                      type="button"
                      onClick={() => setEmailLanguage('es')}
                      data-testid="register-email-lang-es"
                      className={`flex-1 py-2 px-4 rounded-lg border transition-colors font-medium ${
                        emailLanguage === 'es' 
                          ? 'bg-primary text-primary-foreground border-primary' 
                          : 'border-border text-muted-foreground hover:border-primary/50'
                      }`}
                    >
                      Espanol
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  variant="default"
                  fullWidth
                  disabled={loading}
                  className="gold-glow mt-6"
                  data-testid="register-submit-btn"
                >
                  {loading 
                    ? (isSpanish ? 'Creando Cuenta...' : 'Creating Account...') 
                    : (isSpanish ? 'Continuar a Prueba Segura' : 'Continue to Secure Trial')}
                </Button>

                {/* Trust Blurb */}
                <p className="text-xs text-muted-foreground text-center mt-4 leading-relaxed">
                  🔒 {isSpanish 
                    ? 'Se requiere tarjeta de crédito para verificar identidad. Sin cargos por 7 días. Cancela en cualquier momento.'
                    : 'Credit card required to verify identity. No charge for 7 days. Cancel anytime.'}
                </p>
              </form>

              <div className="mt-6 pt-6 border-t border-border">
                <p className="text-center text-sm text-muted-foreground">
                  {isSpanish ? '¿Ya tienes cuenta?' : 'Already have an account?'}{' '}
                  <Link
                    to="/login"
                    className="text-primary hover:underline font-medium"
                  >
                    {isSpanish ? 'Inicia sesión aquí' : 'Sign in here'}
                  </Link>
                </p>
              </div>

              <div className="mt-6 glass rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Icon name="Info" size={20} color="#FFD700" className="flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {isSpanish 
                      ? 'Al crear una cuenta, aceptas nuestros Términos de Servicio y Política de Privacidad. Tus datos están protegidos con encriptación de nivel empresarial.'
                      : 'By creating an account, you agree to our Terms of Service and Privacy Policy. Your data is protected with enterprise-grade encryption.'}
                  </p>
                </div>
              </div>
            </div>

            {/* Trust Signals */}
            <div className="flex items-center justify-center gap-6 text-muted-foreground mt-8">
              <div className="flex items-center gap-2">
                <Icon name="Lock" size={16} />
                <span className="text-xs">{isSpanish ? 'Encriptado SSL' : 'SSL Encrypted'}</span>
              </div>
              <div className="flex items-center gap-2">
                <Icon name="Shield" size={16} />
                <span className="text-xs">{isSpanish ? 'Cumple GDPR' : 'GDPR Compliant'}</span>
              </div>
            </div>

            {/* Footer */}
            <footer className="mt-12 pt-8 border-t border-border">
              <p className="text-center text-sm text-muted-foreground">
                © 2026 GO OUTDOORS LLC™ | LEVERAGING AI FOR THE REST OF US™ | LUMIVETA™
              </p>
            </footer>
          </div>
        </div>
      </div>
      
      {/* Welcome Toast - 4 second display - z-[9999] to appear above disclaimer modal */}
      {showWelcomeToast && (
        <div className="fixed top-4 right-4 z-[9999] animate-in slide-in-from-top-2 fade-in duration-300">
          <div className="bg-primary text-primary-foreground px-6 py-4 rounded-lg shadow-lg flex items-center gap-3 max-w-md">
            <Icon name="CheckCircle" size={24} />
            <p className="text-sm font-medium">{toastMessage}</p>
          </div>
        </div>
      )}
    </>
  );
};

export default Register;
